/**
 * Container API
 * @module api/container
 */

import { xmlToJson, jsonToXml } from '../util/xmlUtils';

let handleNcsItem = () => {};
let handleNcsAppInfo = () => {};
let handleNcsAck = () => {};
let handlePlaceholder = () => {};

const getOrigin = () => {
  if (window.location.origin) {
    return window.location.origin;
  }
  return `${window.location.protocol}//${window.location.host}`;
};

const LUCI_ORIGIN = getOrigin();

export const getNewsroomOrigin = () => {
  const qs = document.location.search.split('+').join(' ');
  const params = {};
  const regex = /[?&]?([^=]+)=([^&]*)/g;

  let tokens = null;
  do {
    tokens = regex.exec(qs);
    if (tokens) {
      params[decodeURIComponent(tokens[1])] = decodeURIComponent(tokens[2]);
    }
  } while (tokens);

  return params.origin;
};

let containerOrigin = getOrigin();
let containerWindow = window.parent;

export const setHandleNcsItem = method => {
  handleNcsItem = method;
};

export const setHandleNcsAppInfo = method => {
  handleNcsAppInfo = method;
};

export const setHandleNcsAck = method => {
  handleNcsAck = method;
};

export const setHandlePlaceholder = method => {
  handlePlaceholder = method;
};

const processMessage = message => {
  let parsedMessage =
    message && (typeof message === 'string' || message instanceof String)
      ? xmlToJson(message)
      : message;

  if (!parsedMessage || (!parsedMessage.mos && !parsedMessage.message)) {
    return;
  }

  const mosMessageHandler = new MosMessageHandler(message);

  console.log(`mosMsgFromHost - Received - ${mosMessageHandler.MessageTypeCode}`);
  console.log(message);

  if (parsedMessage.message) {
    if (parsedMessage.message.$.type !== MessageType.MOS_MSG_FROM_HOST) {
      return;
    }
    parsedMessage = parsedMessage.message;
  }

  console.log(jsonToXml(parsedMessage));

  const mosXml = jsonToXml(
    parsedMessage.mos && parsedMessage.mos.constructor === Array
      ? parsedMessage.mos[0]
      : parsedMessage.mos,
    { rootName: 'mos' }
  );

  switch (mosMessageHandler.MessageTypeCode) {
    case MessageTypeCode.NCS_ACK:
      handleNcsAck(mosXml);
      return 'Ack';
    case MessageTypeCode.NCS_ITEM:
      try {
        handleNcsItem(mosXml);
        sendMosMessageToContainer(Message.NCS_ACK());
      } catch (exception) {
        sendMosMessageToContainer(Message.NCS_ACK(true));
      }
      return 'ItemMessage';
    case MessageTypeCode.NCS_APP_INFO:
      try {
        handleNcsAppInfo(mosXml);
        sendMosMessageToContainer(Message.NCS_ACK());
        if (MosMessageHandler.isPlaceholder(mosXml)) {
          handlePlaceholder(parsedMessage);
        }
      } catch (exception) {
        sendMosMessageToContainer(Message.NCS_ACK(true));
      }
      return 'InfoMessage';
    case MessageTypeCode.PING:
      sendMosMessageToContainer(Message.NCS_ACK());
      return 'Ping';
    case MessageTypeCode.PLACEHOLDER:
      handlePlaceholder(parsedMessage);
      return 'Placeholder';
    case MessageTypeCode.IGNORE:
      return 'Ignore';
    default:
      sendMosMessageToContainer(Message.NCS_ACK());
      return 'Unknown';
  }
};

/**
 * Handles NCS messages from the HTML5 container.
 * @func handleMessage
 * @param  {Event} event Event generated.
 */
export const handleMessage = event => {
  const ncsOrigin = getNewsroomOrigin();

  if (!ncsOrigin) {
    return;
  }

  if (!event.data) {
    return;
  }

  const parsedMessage =
    event.data && (typeof event.data === 'string' || event.data instanceof String)
      ? xmlToJson(event.data)
      : event.data;

  if (!parsedMessage || (!parsedMessage.mos && !parsedMessage.message)) {
    return;
  }

  if (ncsOrigin && ncsOrigin !== event.origin) {
    console.warn(
      `Origin does not match
      event.origin = ${event.origin}
      NCS origin = ${ncsOrigin}`
    );
    return;
  } else if (!ncsOrigin && event.origin !== LUCI_ORIGIN) {
    console.warn(
      `Origin does not match
      event.origin = ${event.origin}
      LUCI_ORIGIN = ${LUCI_ORIGIN}`
    );
    return;
  }

  // Store the source & origin of the postMessage for replies
  containerOrigin = event.origin || containerOrigin;
  containerWindow = window.parent;

  processMessage(parsedMessage);
};

export const initializeContainer = () => {
  window.mosMsgFromHost = mosMsgFromHost;
  // Register the Event Handler – Cross Browser
  if (window.addEventListener) {
    window.addEventListener('message', handleMessage, false);
  } else if (window.attachEvent) {
    window.attachEvent('message', handleMessage, false);
  }
  containerOrigin = getNewsroomOrigin() || containerOrigin;
};

export const requestAppInfo = () => {
  sendMosMessageToContainer('<mos><ncsReqAppInfo /></mos>');
};

/**
 * Handles NCS messages from the ActiveX container.
 * @func mosMsgFromHost
 * @param  {string} message Message
 */
export const mosMsgFromHost = message => {
  processMessage(message);
};

/**
 * Sends a mos message to the container.
 * @func sendMosMessageToContainer
 * @param  {string} message Message to send.
 */
export const sendMosMessageToContainer = message => {
  if (message) {
    if (window.chymox && window.chymox.SendmosMsgFromPlugIn) {
      console.log('Sending Message To ActiveX');
      console.log(message);
      window.chymox.SendmosMsgFromPlugIn(message);
    } else {
      const containerMessage = message;
      console.log(`Sending message to container: containerOrigin = ${containerOrigin}`);
      console.log(containerMessage);
      // alert(`Sending message to container: containerOrigin = ${containerOrigin}`);
      // prompt('Message being sent to the container', message);
      containerWindow.postMessage(containerMessage, containerOrigin);
    }
  }
};

class MosMessageHandler {
  constructor(data) {
    let message = data;
    this.MessageTypeCode = 'UNKNOWN';
    if (typeof message !== 'string' || !(message instanceof String)) {
      message = JSON.stringify(message);
    }
    if (MosMessageHandler.isAckMessage(message)) {
      this.MessageTypeCode = MessageTypeCode.NCS_ACK;
    } else if (MosMessageHandler.isAppInfoMessage(message)) {
      this.MessageTypeCode = MessageTypeCode.NCS_APP_INFO;
    } else if (MosMessageHandler.isEditMessage(message)) {
      this.MessageTypeCode = MessageTypeCode.NCS_ITEM;
    } else if (MosMessageHandler.isFromLuci(message)) {
      this.MessageTypeCode = MessageTypeCode.IGNORE;
    } else if (MosMessageHandler.isPlaceholder(data)) {
      this.MessageTypeCode = MessageTypeCode.PLACEHOLDER;
    }
  }
  static isAckMessage(data) {
    return data && data.includes('ncsAck');
  }

  static isEditMessage(data) {
    return data && (data.includes('ncsItem') || data.includes('mosObj'));
  }

  static isAppInfoMessage(data) {
    return data && data.includes('ncsAppInfo');
  }

  static isFromLuci(data) {
    return data && data.includes('ncsReqAppInfo');
  }

  static isPing(data) {
    return data && data.includes('<ping />');
  }

  static isPlaceholder(data) {
    if (data && data.mos) {
      const item = data.mos.ncsAppInfo ? data.mos.ncsAppInfo.item : data.mos.item;
      return item[0] && item[0].objID[0] === 'XXX';
    }

    return data && data.includes('<objID>XXX</objID>');
  }
}

const Message = {
  NCS_ACK: (error, errorDescription) => {
    const status = error ? 'ERROR' : 'ACK';
    const statusDescription = errorDescription
      ? `<statusDescription>${statusDescription}</statusDescription>`
      : '';
    return `<mos><ncsAck><status>${status}</status>${statusDescription}</ncsAck></mos>`;
  },
  NCS_NACK: '<mos><ncsAck><status>NACK</status></ncsAck></mos>'
};

const MessageType = {
  ASSET_SELECTED_CONFIRMED: 'assetselectedconfirmed',
  DEFICIENT_ASSET_CREATED: 'deficientassetcreated',
  COMPOSITE_CREATED: 'compositecreated',
  MOS_MSG_FROM_HOST: 'mosmsgfromhost'
};

const MessageTypeCode = {
  NCS_ACK: 'NCS_ACK',
  NCS_ITEM: 'NCS_ITEM',
  NCS_APP_INFO: 'NCS_APP_INFO',
  PING: 'PING',
  PLACEHOLDER: 'PLACEHOLDER',
  IGNORE: 'IGNORE'
};



// WEBPACK FOOTER //
// ./app/api/container.js